import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Building2, Home, Droplets, MapPin, ShieldCheck, Wallet, ArrowRight, FileText, CheckCircle2, TrendingUp,
  Landmark, Smartphone, CreditCard, Store, Globe, QrCode, Zap, BarChart3, ChevronLeft, ChevronRight,
} from "lucide-react";
import PunjabEmblem from "@/components/PunjabEmblem";

const PMIDC_IMG = "https://customer-assets-wrfwihn1.emergentagent.net/job_ulb-tax-platform/artifacts/ki5z73xv_pmidc_main.webp";
const CHANDIGARH_IMG = "https://images.unsplash.com/photo-1682569295668-eecf20fedf33?fm=jpg&q=80&w=2400&auto=format&fit=crop";
const LUDHIANA_IMG = "https://images.unsplash.com/photo-1660655540384-929e16cde076?fm=jpg&q=80&w=2400&auto=format&fit=crop";
const WAGAH_IMG = "https://images.unsplash.com/photo-1651910031211-42aaa52b87cc?fm=jpg&q=80&w=2400&auto=format&fit=crop";
const PATIALA_IMG = "https://images.unsplash.com/photo-1587474260584-136574528ed5?fm=jpg&q=80&w=2400&auto=format&fit=crop";
const CITIZEN_IMG = LUDHIANA_IMG;
const GIS_IMG = CHANDIGARH_IMG;

const HERO_SLIDES = [
  {
    src: PMIDC_IMG,
    city: "Chandigarh",
    label: "Municipal administration",
    sub: "Civic services for urban local bodies",
  },
  {
    src: CHANDIGARH_IMG,
    city: "Chandigarh",
    label: "Chandigarh from above",
    sub: "The joint capital of Punjab and Haryana at dusk",
  },
  {
    src: PATIALA_IMG,
    city: "Chandigarh",
    label: "Punjab & Haryana Legislative Capitol",
    sub: "Le Corbusier's Capitol Complex · seat of state governance",
  },
  {
    src: LUDHIANA_IMG,
    city: "Ludhiana",
    label: "Ludhiana's civic streets",
    sub: "Punjab's largest municipal corporation and industrial hub",
  },
  {
    src: WAGAH_IMG,
    city: "Attari-Wagah",
    label: "Attari-Wagah border ceremony",
    sub: "Where Punjab meets the world every evening",
  },
];

const PAYMENT_CHANNELS = [
  { icon: Smartphone, label: "Instant transfer", note: "Mobile apps" },
  { icon: QrCode, label: "QR Code", note: "Scan to settle" },
  { icon: CreditCard, label: "Card", note: "Card channels" },
  { icon: Globe, label: "Online transfer", note: "Bank channels" },
  { icon: Zap, label: "Bill aggregators", note: "Bill networks" },
  { icon: Landmark, label: "Bank transfer", note: "Virtual accounts" },
  { icon: Store, label: "Service counter", note: "Walk-in" },
  { icon: Smartphone, label: "Mobile App", note: "Android · iOS" },
];

function HeroCarousel() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);
  const go = (n) => setIdx((n + HERO_SLIDES.length) % HERO_SLIDES.length);
  return (
    <>
      {HERO_SLIDES.map((s, i) => (
        <img
          key={s.src}
          src={s.src}
          alt={s.label}
          className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-1000 ${i === idx ? "opacity-40 dark:opacity-25" : "opacity-0"}`}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/70 to-white dark:from-slate-950/40 dark:via-slate-950/80 dark:to-slate-950 pointer-events-none" />
      {/* Location tag bottom-left */}
      <div className="absolute left-4 bottom-4 sm:left-8 sm:bottom-8 z-10 max-w-md" data-testid="hero-slide-caption">
        <div className="inline-flex items-center gap-2 bg-white/90 dark:bg-slate-900/70 backdrop-blur px-3 py-1.5 rounded-full border border-slate-200/80 dark:border-slate-700 text-xs">
          <MapPin className="w-3.5 h-3.5 text-[#E8702A]" />
          <span className="font-semibold tracking-wider uppercase text-[10px]">{HERO_SLIDES[idx].city}</span>
          <span className="text-slate-500">·</span>
          <span className="text-slate-600 dark:text-slate-300">{HERO_SLIDES[idx].label}</span>
        </div>
        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5 pl-2">{HERO_SLIDES[idx].sub}</div>
      </div>
      {/* Controls */}
      <div className="absolute right-4 bottom-4 sm:right-8 sm:bottom-8 z-10 flex items-center gap-2">
        <button onClick={() => go(idx - 1)} data-testid="hero-prev" className="w-8 h-8 rounded-full bg-white/80 dark:bg-slate-900/70 border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-white transition">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-1.5">
          {HERO_SLIDES.map((slide, i) => (
            <button
              key={`hero-dot-${slide.src}`}
              onClick={() => go(i)}
              data-testid={`hero-dot-${i}`}
              aria-label={`Show slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-[#0F2942] dark:bg-white" : "w-1.5 bg-slate-400/60"}`}
            />
          ))}
        </div>
        <button onClick={() => go(idx + 1)} data-testid="hero-next" className="w-8 h-8 rounded-full bg-white/80 dark:bg-slate-900/70 border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-white transition">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="tricolor-bar" />

      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/85 dark:bg-slate-900/85 backdrop-blur-md border-b border-slate-200/70 dark:border-slate-800/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
            <PunjabEmblem size={40} />
            <div>
              <div className="font-heading font-semibold text-slate-900 dark:text-slate-100 text-base leading-tight">PULB DRMS</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                <span>Municipal Revenue Platform</span>
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" data-testid="landing-btn-login" className="text-slate-700 dark:text-slate-200">
                Sign in
              </Button>
            </Link>
            <Link to="/register">
              <Button data-testid="landing-btn-register" className="bg-[#E8702A] hover:bg-[#d95f1c] text-white">
                Register as Citizen
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden min-h-[640px] sm:min-h-[720px]">
        <HeroCarousel />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-[#0F2942] dark:text-slate-300 bg-white/85 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-full px-3 py-1" data-testid="hero-badge">
              <ShieldCheck className="w-3.5 h-3.5" /> Municipal Revenue Platform
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight text-slate-900 dark:text-white">
              End-to-end digital revenue<br />
              <span className="text-[#0F2942] dark:text-[#4A7BAA]">for every Urban Local Body.</span>
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-slate-600 dark:text-slate-300 max-w-2xl">
              Assessment · billing · collection · reconciliation — one unified platform with role-based dashboards for officers and self-service tools for citizens.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login">
                <Button size="lg" className="bg-[#0F2942] hover:bg-[#1E3A5F] text-white" data-testid="hero-btn-officer">
                  Officer Login <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/register">
                <Button size="lg" variant="outline" className="border-slate-300 dark:border-slate-700 bg-white/60 dark:bg-slate-900/40" data-testid="hero-btn-citizen">
                  Citizen Sign Up
                </Button>
              </Link>
            </div>
          </div>

          {/* Stat strip */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl" data-testid="hero-stats">
            {[
              { n: "100+", l: "Urban Local Bodies" },
              { n: "50K+", l: "Properties Managed" },
              { n: "8", l: "Payment Channels" },
              { n: "24×7", l: "Auto Reconciliation" },
            ].map((s) => (
              <div key={s.l} className="bg-white/90 dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-4">
                <div className="text-2xl font-heading font-bold text-[#0F2942] dark:text-white tabular-nums">{s.n}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proposed solution pillars */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="mb-10">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#E8702A]">Solution Pillars</div>
          <h2 className="mt-2 text-3xl font-heading font-semibold text-slate-900 dark:text-white">Everything modern municipal revenue demands</h2>
          <p className="mt-3 text-slate-600 dark:text-slate-400 max-w-3xl">A reference architecture for end-to-end digital revenue management across urban local bodies.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: Wallet, title: "End-to-end digital assessment", desc: "Property, water, sewerage, advertisement and license — assessed, billed and collected in one flow." },
            { icon: CreditCard, title: "Multiple collection channels", desc: "Online, QR, counter and mobile app channels in a single workflow." },
            { icon: BarChart3, title: "Centralised MIS", desc: "Higher-authority dashboards with real-time collection efficiency per municipal body." },
            { icon: Landmark, title: "Real-time reconciliation", desc: "Auto-reconciliation with the municipal body's virtual account and webhook-driven settlement reports." },
            { icon: MapPin, title: "GIS & Geo-tagging", desc: "Every property mapped on Leaflet + OSM for asset visibility and inspection routing." },
            { icon: ShieldCheck, title: "Transparency & audit", desc: "Complete audit trail, role-based access, and citizen-facing service delivery SLAs." },
          ].map((f) => (
            <div key={f.title} className="hover-lift bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6" data-testid={`pillar-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`}>
              <div className="w-11 h-11 rounded-lg bg-[#0F2942]/10 dark:bg-[#0F2942]/40 flex items-center justify-center">
                <f.icon className="w-5 h-5 text-[#0F2942] dark:text-white" strokeWidth={1.75} />
              </div>
              <h3 className="mt-4 font-heading font-semibold text-slate-900 dark:text-slate-100">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Payment channels showcase */}
      <section className="py-16 bg-white dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#0D7650]">Collection channels</div>
            <h2 className="mt-2 text-3xl font-heading font-semibold text-slate-900 dark:text-white">Many channels. Automatic reconciliation.</h2>
            <p className="mt-3 text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">Every collection channel is settled into the municipal body's virtual account and reconciled in real time.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="payment-channels-grid">
            {PAYMENT_CHANNELS.map((c) => (
              <div key={c.label} className="hover-lift border border-slate-200 dark:border-slate-700 rounded-xl p-5 text-center bg-slate-50 dark:bg-slate-950/40">
                <div className="w-11 h-11 mx-auto rounded-lg bg-[#0F2942] flex items-center justify-center">
                  <c.icon className="w-5 h-5 text-white" strokeWidth={1.75} />
                </div>
                <div className="mt-3 font-heading font-semibold text-sm">{c.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{c.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature split — GIS */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-[#0D7650]">GIS Mapping</div>
            <h2 className="mt-2 text-3xl font-heading font-semibold text-slate-900 dark:text-white">Every property on the map</h2>
            <p className="mt-4 text-slate-600 dark:text-slate-400 leading-relaxed">
              Ward-level property intelligence with Leaflet + OpenStreetMap. Officers geo-tag properties, filter by ward or zone, and inspect outstanding dues at a glance.
            </p>
            <ul className="mt-6 space-y-3">
              {["Property layers by ward and zone", "Geo-tagged property inspections", "Search by coordinates or plot number"].map((x) => (
                <li key={x} className="flex items-center gap-3 text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="w-4 h-4 text-[#0D7650]" /> {x}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-lg">
            <img src={GIS_IMG} alt="Aerial view of a Punjab city at dusk" className="w-full h-80 object-cover" />
          </div>
        </div>
      </section>

      {/* Feature split — Citizen */}
      <section className="py-16 bg-white dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-lg">
            <img src={CITIZEN_IMG} alt="Ludhiana civic streets — Punjab municipal life" className="w-full h-80 object-cover" />
          </div>
          <div className="order-1 lg:order-2">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#E8702A]">Citizen Portal</div>
            <h2 className="mt-2 text-3xl font-heading font-semibold text-slate-900 dark:text-white">Civic services in your pocket</h2>
            <p className="mt-4 text-slate-600 dark:text-slate-400 leading-relaxed">
              Register once, manage all your properties, track dues, download receipts, submit building plans, and raise complaints — anytime, on any device.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <TrendingUp className="w-4 h-4 text-[#0F2942]" />
              <div className="text-sm text-slate-700 dark:text-slate-300">Collection efficiency up 38% since digital rollout</div>
            </div>
            <Link to="/register" className="inline-block mt-6">
              <Button className="bg-[#0F2942] hover:bg-[#1E3A5F] text-white" data-testid="citizen-cta-register">Get started</Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 dark:border-slate-800 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <PunjabEmblem size={22} />
            <span>© {new Date().getFullYear()} PULB Digital Revenue Management System</span>
          </div>
          <div>Urban Local Bodies Revenue Platform</div>
        </div>
      </footer>
    </div>
  );
}
